"use client";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import { FiTrash2 } from "react-icons/fi";
import { createPortal } from "react-dom";
import VarianceModal from "./BudgetVarianceModal";
import { TrashIcon } from "@heroicons/react/20/solid";

// Custom styles for react-select
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    height: "32px",
    fontSize: "12px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
    "&:focus": {
      outline: "none",
      boxShadow: "none",
      border: "none",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
};

export default function BudgetTab({ project, setProject, onGeneratePO }) {
  const navigate = useNavigate();
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [budgetTitle, setBudgetTitle] = useState("");
  const [budgetFile, setBudgetFile] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [expandedBudget, setExpandedBudget] = useState(null);
  const [editingBudget, setEditingBudget] = useState(null);
  const [editedItems, setEditedItems] = useState([]);
  const [showVarianceModal, setShowVarianceModal] = useState(false);
  const [selectedLineItems, setSelectedLineItems] = useState([]);
  const [selectedBudgets, setSelectedBudgets] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [dialog, setDialog] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const [isAddingToVariance, setIsAddingToVariance] = useState(false);
  const [selectedVarianceId, setSelectedVarianceId] = useState(null);
  const [filters, setFilters] = useState({
    bidArea: null,
    vendor: null,
    category: null,
    section: null,
    descriptionSearch: "",
    uom: null, // <-- NEW
    size: null, // <-- NEW
    poWo: null, // ← NEW: holds PO or WO number (or null)
  });
  const [showFilter, setShowFilter] = useState(null);
  const uomRef = useRef(null);
  const sizeRef = useRef(null);
  // Refs for filter inputs
  const sectionRef = useRef(null);
  const categoryRef = useRef(null);
  const vendorRef = useRef(null);
  const bidAreaRef = useRef(null);
  const descriptionRef = useRef(null);
  const poWoRef = useRef(null);
  // Handle click outside to close filter dropdowns

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sectionRef.current &&
        !sectionRef.current.contains(event.target) &&
        categoryRef.current &&
        !categoryRef.current.contains(event.target) &&
        vendorRef.current &&
        !vendorRef.current.contains(event.target) &&
        bidAreaRef.current &&
        !bidAreaRef.current.contains(event.target) &&
        descriptionRef.current &&
        !descriptionRef.current.contains(event.target) &&
        uomRef.current &&
        !uomRef.current.contains(event.target) &&
        sizeRef.current &&
        !sizeRef.current.contains(event.target) &&
        poWoRef.current &&
        !poWoRef.current.contains(event.target) // ← NEW
      ) {
        setShowFilter(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          "https://erp-production-72da01c8e651.herokuapp.com/api/accounts/vendors",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const vendorData = response.data.filter((v) => v.type === "vendor");
        setVendors(vendorData);
      } catch (err) {
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message:
            "Failed to fetch vendors: " +
            (err.response?.data?.message || err.message),
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
      }
    };
    fetchVendors();
  }, []);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const getFilterOptions = (field) => {
    if (!expandedBudget) return [];
    const budget = project?.budgets?.find((b) => b._id === expandedBudget);
    const rawItems =
      editingBudget === expandedBudget ? editedItems : budget?.lineItems || [];
    if (!rawItems.length) return [];

    // Apply all filters EXCEPT the one for the current field
    const filteredForOptions = rawItems.filter((item) => {
      // Skip the filter for the field we're generating options for
      if (
        field !== "category" &&
        filters.category &&
        item.category !== filters.category
      )
        return false;
      if (
        field !== "section" &&
        filters.section &&
        item.sectionNumber !== filters.section
      )
        return false;
      if (
        field !== "bidArea" &&
        filters.bidArea &&
        item.bidArea !== filters.bidArea
      )
        return false;
      if (field !== "vendor" && filters.vendor) {
        const vendorId =
          item.vendor && typeof item.vendor === "object"
            ? item.vendor._id
            : item.vendor;
        if (item.category !== "M" || vendorId !== filters.vendor) return false;
      }
      if (field !== "descriptionSearch" && filters.descriptionSearch) {
        const desc = (
          item.itemDescription ||
          item.costCodeDescription ||
          ""
        ).toLowerCase();
        if (!desc.includes(filters.descriptionSearch.toLowerCase()))
          return false;
      }
      if (field !== "uom" && filters.uom) {
        const uom = item.category === "M" ? item.container : item.uom;
        if (uom !== filters.uom) return false;
      }
      if (field !== "size" && filters.size && item.size !== filters.size)
        return false;
      if (field !== "poWo" && filters.poWo) {
        const isPO = filters.poWo.startsWith("PO-");
        const isWO = filters.poWo.startsWith("WO-");
        const number = filters.poWo.slice(3);
        let matched = false;
        if (isPO) {
          matched = project.purchaseOrders?.some(
            (po) =>
              po.poNumber === number &&
              po.lineItems?.some((li) => {
                const ref =
                  typeof li.budgetItem === "object"
                    ? li.budgetItem?._id
                    : li.budgetItem;
                return ref === item._id;
              })
          );
        } else if (isWO) {
          matched = project.workOrders?.some(
            (wo) =>
              wo.woNumber === number &&
              wo.lineItems?.some((li) => {
                const ref =
                  typeof li.budgetItem === "object"
                    ? li.budgetItem?._id
                    : li.budgetItem;
                return ref === item._id;
              })
          );
        }
        if (!matched) return false;
      }
      return true;
    });

    // Now extract unique values from the filtered subset
    let uniqueValues = [];

    switch (field) {
      case "vendor":
        const vendorIds = [
          ...new Set(
            filteredForOptions
              .filter((i) => i.category === "M" && i.vendor)
              .map((i) =>
                typeof i.vendor === "object" ? i.vendor._id : i.vendor
              )
              .filter(Boolean)
          ),
        ];
        return [
          { value: null, label: "Unassigned" },
          ...vendorIds.map((id) => ({
            value: id,
            label: vendors.find((v) => v._id === id)?.companyName || "Unknown",
          })),
        ];

      case "uom":
        uniqueValues = [
          ...new Set(
            filteredForOptions
              .map((i) => (i.category === "M" ? i.container : i.uom) || "")
              .filter(Boolean)
          ),
        ];
        break;

      case "size":
        uniqueValues = [
          ...new Set(
            filteredForOptions.map((i) => i.size || "").filter(Boolean)
          ),
        ];
        break;

      case "poWo":
        const poSet = new Set();
        const woSet = new Set();

        project.purchaseOrders?.forEach((po) => {
          const hasItem = po.lineItems?.some((li) => {
            const ref =
              typeof li.budgetItem === "object"
                ? li.budgetItem?._id
                : li.budgetItem;
            return filteredForOptions.some((i) => i._id === ref);
          });
          if (hasItem && po.poNumber) poSet.add(po.poNumber);
        });

        project.workOrders?.forEach((wo) => {
          const hasItem = wo.lineItems?.some((li) => {
            const ref =
              typeof li.budgetItem === "object"
                ? li.budgetItem?._id
                : li.budgetItem;
            return filteredForOptions.some((i) => i._id === ref);
          });
          if (hasItem && wo.woNumber) woSet.add(wo.woNumber);
        });

        const poOptions = Array.from(poSet).map((n) => ({
          value: `PO-${n}`,
          label: `PO-${n}`,
        }));
        const woOptions = Array.from(woSet).map((n) => ({
          value: `WO-${n}`,
          label: `WO-${n}`,
        }));
        return [...poOptions, ...woOptions].sort((a, b) =>
          a.label.localeCompare(b.label)
        );

      default:
        // bidArea, section, category
        uniqueValues = [
          ...new Set(filteredForOptions.map((i) => i[field]).filter(Boolean)),
        ];
        break;
    }

    return uniqueValues.map((v) => ({ value: v, label: v || "N/A" }));
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    // Force re-render of dropdowns by closing/opening if needed
    if (filterType !== "descriptionSearch") {
      setShowFilter(null);
    }
  };

  const clearAllFilters = () => {
    setFilters({
      bidArea: null,
      vendor: null,
      category: null,
      section: null,
      descriptionSearch: "",
      uom: null,
      size: null,
      poWo: null, // ← NEW
    });
    setSelectedLineItems([]);
    setShowFilter(null);
  };

  const toggleFilter = (field) => {
    setShowFilter((prev) => (prev === field ? null : field));
  };

  const handleFileUpload = () => {
    if (!budgetFile || !budgetTitle.trim()) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Please provide a title and select a file.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    const fileExtension = budgetFile.name.split(".").pop().toLowerCase();
    const processFile = async (data) => {
      const cleanNumber = (value) => {
        if (value == null) return 0;
        const str = String(value).trim();
        const cleaned = str.replace(/[$,]/g, "");
        return parseFloat(cleaned) || 0;
      };
      const parsedItems = data
        .filter((row) => ["M", "L"].includes(row["Category"]))
        .map((row) => {
          let vendorId = null;
          if (row["Category"] === "M" && row["Part Number"]?.trim()) {
            const vendorName = row["Part Number"].trim();
            const matchedVendors = vendors.filter((v) =>
              v.companyName.toLowerCase().includes(vendorName.toLowerCase())
            );
            if (matchedVendors.length === 1) {
              vendorId = matchedVendors[0]._id;
            } else if (matchedVendors.length > 1) {
              vendorId = matchedVendors[0]._id;
            }
          }
          if (row["Category"] === "M" && row["Vendor"]?.trim()) {
            const vendorName = row["Vendor"].trim();
            const matchedVendors = vendors.filter((v) =>
              v.companyName.toLowerCase().includes(vendorName.toLowerCase())
            );
            if (matchedVendors.length === 1) {
              vendorId = matchedVendors[0]._id;
            } else if (matchedVendors.length > 1) {
              vendorId = matchedVendors[0]._id;
            }
          }
          const unitPrice = cleanNumber(row[" Price "] || row["Price"]);
          const sellingPrice = cleanNumber(
            row["Customer Price"] || row[" Price "] || row["Price"]
          );
          const amount = cleanNumber(row[" Amount "] || row["Amount"]);
          let quantity;
          if (row["Category"] === "M") {
            quantity =
              amount > 0 && unitPrice > 0
                ? amount / unitPrice <= 1
                  ? 1
                  : Math.round(amount / unitPrice)
                : 0;
          } else {
            quantity = Math.round(cleanNumber(row["Quantity"])) || 0;
          }
          const unitRate =
            row["Category"] === "L" && quantity > 0
              ? Number((amount / quantity).toFixed(2))
              : 0;
          return {
            sectionNumber: row["Section #"] || "",
            category: row["Category"] || "",
            costCodeDescription: row["Cost Code Description"] || "",
            amount: Number(amount.toFixed(2)),
            vendor: vendorId,
            uom:
              row["Category"] === "M"
                ? row["Container"] || row["UOM"] || "Sheet"
                : row["UOM"] || "",
            ...(row["Category"] === "M" && {
              itemCode: row["Item Code"] || "",
              itemDescription: row["Item Description"] || "",
              size: row["Size"] || "",
              requiredQty: quantity,
              container: row["Container"] || "",
              bidArea: row["Bid Area"] || "",
              price: sellingPrice.toFixed(2),
              cost: unitPrice,
            }),
            ...(row["Category"] === "L" && {
              quantity: quantity,
              uom: row["UOM"] || "",
              laborHours: cleanNumber(row["Labor Hours"]) || 0,
              unitRate: unitRate,
              price: unitRate,
              bidArea: row["Bid Area"] || "",
            }),
          };
        });
      if (parsedItems.length === 0) {
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message:
            "No valid material (M) or labor (L) items found in the uploaded file.",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }
      uploadBudget(parsedItems);
    };
    if (fileExtension === "csv") {
      Papa.parse(budgetFile, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => processFile(result.data),
        error: (err) =>
          setDialog({
            open: true,
            type: "error",
            title: "Error",
            message: "Error parsing CSV file: " + err.message,
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          }),
      });
    } else if (fileExtension === "xlsx") {
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        processFile(jsonData);
      };
      reader.onerror = () =>
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message: "Error reading XLSX file.",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
      reader.readAsArrayBuffer(budgetFile);
    } else {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Unsupported file format. Please upload a .csv or .xlsx file.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const uploadBudget = async (lineItems) => {
    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}/budgets`,
        { title: budgetTitle, lineItems },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updatedProjectResponse = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(updatedProjectResponse.data);
      setBudgetTitle("");
      setBudgetFile(null);
      setShowBudgetForm(false);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Budget uploaded successfully!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to upload budget: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmBudgetPrompt = (budgetId) => {
    setDialog({
      open: true,
      type: "warning",
      title: "Confirm Budget",
      message:
        "Are you sure you want to confirm this budget? Once confirmed, no further changes can be made directly to this budget. Any future modifications will require creating a variance.",
      confirmText: "Yes, Confirm",
      cancelText: "No, Cancel",
      onConfirm: async () => {
        setDialog((prev) => ({ ...prev, open: false }));
        if (editingBudget === budgetId) {
          await saveEditedBudget();
        }
        handleConfirmBudget(budgetId);
      },
      onClose: () => setDialog((prev) => ({ ...prev, open: false })),
    });
  };

  const handleConfirmBudget = async (budgetId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}/budgets/${budgetId}/confirm`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updatedProjectResponse = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(updatedProjectResponse.data);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Budget confirmed successfully!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to confirm budget: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleDeleteBudgetPrompt = (budgetId) => {
    setDialog({
      open: true,
      type: "warning",
      title: "Delete Budget",
      message:
        "Are you sure you want to delete this budget? This action cannot be undone.",
      confirmText: "Yes, Delete",
      cancelText: "No, Cancel",
      onConfirm: () => {
        setDialog((prev) => ({ ...prev, open: false }));
        handleDeleteBudget(budgetId);
      },
      onClose: () => setDialog((prev) => ({ ...prev, open: false })),
    });
  };

  const handleDeleteBudget = async (budgetId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}/budgets/${budgetId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updatedProjectResponse = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(updatedProjectResponse.data);
      setExpandedBudget(null);
      setEditingBudget(null);
      setEditedItems([]);
      setSelectedLineItems([]);
      setSelectedBudgets(selectedBudgets.filter((id) => id !== budgetId));
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Budget deleted successfully!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to delete budget: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleSelectBudget = (budgetId) => {
    setSelectedBudgets((prev) =>
      prev.includes(budgetId)
        ? prev.filter((id) => id !== budgetId)
        : [...prev, budgetId]
    );
  };

  const handleConvertMultipleToChangeOrder = () => {
    if (selectedBudgets.length === 0) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Please select at least one budget to convert to a change order.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    const selectedBudgetsData = budgets.filter((b) =>
      selectedBudgets.includes(b._id)
    );
    const isTM = selectedBudgetsData.every(
      (b) => b.isTimeAndMaterial && !b.isVariance
    );
    const isVariance = selectedBudgetsData.every(
      (b) => b.isVariance && !b.isTimeAndMaterial
    );
    const originalBudgetIds = [
      ...new Set(
        selectedBudgetsData
          .filter((b) => b.isVariance)
          .map((b) => b.originalBudgetId?.toString())
      ),
    ];
    if (!isTM && !isVariance) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Selected budgets must all be either T&M or variance budgets, not a mix.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    if (isVariance && originalBudgetIds.length > 1) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "All variance budgets must belong to the same original budget.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    setDialog({
      open: true,
      type: "warning",
      title: "Convert Budgets to Change Order",
      message: `Are you sure you want to convert ${selectedBudgets.length} ${
        isTM ? "T&M" : "variance"
      } budget${
        selectedBudgets.length > 1 ? "s" : ""
      } to a single change order? The selected budgets will be removed to avoid duplication.`,
      confirmText: "Yes, Convert",
      cancelText: "No, Cancel",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("token");
          const endpoint = `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}/budgets/multiple-to-change-order`;
          const response = await axios.post(
            endpoint,
            {
              budgetIds: selectedBudgets,
              title: `Change Order from ${isTM ? "T&M" : "Variance"} Budgets`,
              billable: true,
              changesToSchedule: 0,
              overheadPercentage: isTM ? undefined : 0,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setProject(response.data);
          setSelectedBudgets([]);
          setDialog({
            open: true,
            type: "success",
            title: "Success",
            message: "Budgets converted to change order successfully!",
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
        } catch (err) {
          setDialog({
            open: true,
            type: "error",
            title: "Error",
            message:
              "Failed to convert budgets to change order: " +
              (err.response?.data?.message || err.message),
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
        }
      },
      onClose: () => setDialog((prev) => ({ ...prev, open: false })),
    });
  };

  const handleConvertToChangeOrder = async (budget, originalBudgetId) => {
    const isTMBudget = budget.isTimeAndMaterial;
    setDialog({
      open: true,
      type: "warning",
      title: "Convert to Change Order",
      message: isTMBudget
        ? "Are you sure you want to convert this T&M budget to a change order? The entire T&M budget will be converted, and the budget will be removed to avoid duplication."
        : budget.isVariance
        ? "Are you sure you want to convert this variance to a change order? The variance will be removed to avoid duplication."
        : "Are you sure you want to convert this budget to a change order?",
      confirmText: "Yes, Convert",
      cancelText: "No, Cancel",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("token");
          const endpoint = budget.isVariance
            ? `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}/budgets/${originalBudgetId}/variance/${budget._id}/convert-to-change-order`
            : `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}/budgets/${budget._id}/convert-to-change-order`;
          const response = await axios.post(
            endpoint,
            {
              title: `${budget.title} - Change Order`,
              billable: true,
              changesToSchedule: 0,
              overheadPercentage: 0,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const updatedProject = {
            ...response.data,
            purchaseOrders: response.data.purchaseOrders || [],
            workOrders: response.data.workOrders || [],
          };
          setProject(updatedProject);
          setSelectedBudgets(selectedBudgets.filter((id) => id !== budget._id));
          setDialog({
            open: true,
            type: "success",
            title: "Success",
            message: "Budget converted to change order successfully!",
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
        } catch (err) {
          setDialog({
            open: true,
            type: "error",
            title: "Error",
            message:
              "Failed to convert budget to change order: " +
              (err.response?.data?.message || err.message),
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
        }
      },
      onClose: () => setDialog((prev) => ({ ...prev, open: false })),
    });
  };

  const handleEditBudget = (budgetId) => {
    const budget = budgets.find((b) => b._id === budgetId);
    setEditingBudget(budgetId);
    const normalizedItems = budget.lineItems.map((item) => {
      let vendorId = null;
      if (item.vendor) {
        if (typeof item.vendor === "object" && item.vendor._id) {
          vendorId = item.vendor._id;
        } else if (typeof item.vendor === "string") {
          vendorId = item.vendor;
        }
      }
      const unitRate =
        item.category === "L" && item.quantity > 0
          ? Number((item.amount / item.quantity).toFixed(2))
          : item.unitRate || 0;
      return {
        ...item,
        vendor: vendorId,
        ...(item.category === "L" && { unitRate }),
      };
    });
    setEditedItems([...normalizedItems]);
    setExpandedBudget(budgetId);
  };

  const handleCancelEdit = () => {
    setEditingBudget(null);
    setEditedItems([]);
    setExpandedBudget(null);
    setSelectedLineItems([]);
    clearAllFilters();
  };

  const handleItemChange = (index, field, value) => {
    setEditedItems((prev) => {
      const updatedItems = [...prev];
      const item = updatedItems[index];
      if (field === "vendor" || field === "bidArea") {
        updatedItems[index][field] = value;
      } else {
        let numericValue = parseFloat(value) || 0;
        if (item.category === "M" && field === "requiredQty") {
          numericValue = Math.round(numericValue);
        }
        updatedItems[index][field] = numericValue;
        if (item.category === "M") {
          if (field === "requiredQty" || field === "cost") {
            updatedItems[index].amount =
              updatedItems[index].requiredQty * updatedItems[index].cost;
          }
        } else if (item.category === "L") {
          if (field === "quantity" || field === "unitRate") {
            updatedItems[index].amount =
              updatedItems[index].quantity * updatedItems[index].unitRate;
            updatedItems[index].unitRate = Number(
              updatedItems[index].unitRate.toFixed(2)
            );
          }
        }
      }
      return updatedItems;
    });
  };

  const handleRemoveItem = (index) => {
    setEditedItems((prev) => prev.filter((_, i) => i !== index));
  };

  const saveEditedBudget = async () => {
    try {
      const token = localStorage.getItem("token");
      const budgetToUpdate = budgets.find((b) => b._id === editingBudget);
      const normalizedLineItems = editedItems.map((item) => ({
        ...item,
        vendor: item.category === "M" ? item.vendor || null : null,
        amount:
          item.category === "M"
            ? Number((item.requiredQty * item.cost).toFixed(2))
            : Number((item.quantity * item.unitRate).toFixed(2)),
      }));
      await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}/budgets/${editingBudget}`,
        {
          title: budgetToUpdate.title,
          lineItems: normalizedLineItems,
          status: budgetToUpdate.status,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updatedProjectResponse = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(updatedProjectResponse.data);
      setEditingBudget(null);
      setEditedItems([]);
      setExpandedBudget(null);
      setSelectedLineItems([]);
      clearAllFilters();
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Budget saved successfully with your changes!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to save budget changes: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleSelectLineItem = (item) => {
    setSelectedLineItems((prev) =>
      prev.some((i) => i._id === item._id)
        ? prev.filter((i) => i._id !== item._id)
        : [...prev, item]
    );
  };

  const handleCreateVariances = () => {
    if (selectedLineItems.length === 0) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Please select at least one line item to create variances.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    const budget = budgets.find((b) => b._id === expandedBudget);
    if (budget.isChangeOrder) {
      const hasCriticalChanges = selectedLineItems.some(
        (item) =>
          (item.category === "M" &&
            (item.requiredQty !== undefined ||
              item.cost !== undefined ||
              item.price !== undefined)) ||
          (item.category === "L" &&
            (item.quantity !== undefined || item.unitRate !== undefined))
      );
      if (hasCriticalChanges) {
        setDialog({
          open: true,
          type: "warning",
          title: "Variance Impacts Change Order",
          message:
            "Creating a variance for this change order budget will update the associated change order's quantities, costs, or prices. Do you want to proceed?",
          confirmText: "Yes, Proceed",
          cancelText: "No, Cancel",
          onConfirm: () => {
            setDialog((prev) => ({ ...prev, open: false }));
            setIsAddingToVariance(false);
            setShowVarianceModal(true);
          },
          onClose: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }
    }
    setIsAddingToVariance(false);
    setShowVarianceModal(true);
  };

  // Add a utility function to apply filters to any line items array
  // === ADD THIS: Enhanced applyFiltersToItems with PO/WO support ===
  const applyFiltersToItems = (items) => {
    return items.filter((item) => {
      // === Category ===
      if (filters.category && item.category !== filters.category) return false;

      // === Section ===
      if (filters.section && item.sectionNumber !== filters.section)
        return false;

      // === Bid Area ===
      if (filters.bidArea && item.bidArea !== filters.bidArea) return false;

      // === Vendor ===
      if (filters.vendor) {
        const vendorId =
          item.vendor && typeof item.vendor === "object"
            ? item.vendor._id
            : item.vendor;
        if (item.category !== "M" || vendorId !== filters.vendor) return false;
      }

      // === Description Search ===
      if (filters.descriptionSearch) {
        const desc = (
          item.itemDescription ||
          item.costCodeDescription ||
          ""
        ).toLowerCase();
        if (!desc.includes(filters.descriptionSearch.toLowerCase()))
          return false;
      }

      // === UOM ===
      if (filters.uom) {
        const uom = item.category === "M" ? item.container : item.uom;
        if (uom !== filters.uom) return false;
      }

      // === Size ===
      if (filters.size && item.size !== filters.size) return false;

      // === PO / WO Filter ===
      if (filters.poWo) {
        const selectedValue = filters.poWo; // e.g., "PO-1001" or "WO-500"
        const isPO = selectedValue.startsWith("PO-");
        const isWO = selectedValue.startsWith("WO-");
        const selectedNumber = selectedValue.slice(3).trim(); // "1001"

        let matched = false;

        if (isPO) {
          matched = (project.purchaseOrders || []).some((po) => {
            // Normalize po.poNumber: extract number part
            const poNumberStr = String(po.poNumber || "").trim();
            const poNumber = poNumberStr.replace(/^(PO[-_\s]*)/i, "").trim();

            if (poNumber !== selectedNumber) return false;

            // Check if any line item links to current budget item
            return (po.lineItems || []).some((li) => {
              if (!li.budgetItem) return false;
              const ref =
                typeof li.budgetItem === "object"
                  ? li.budgetItem._id
                  : li.budgetItem;
              return ref === item._id;
            });
          });
        } else if (isWO) {
          matched = (project.workOrders || []).some((wo) => {
            const woNumberStr = String(wo.woNumber || "").trim();
            const woNumber = woNumberStr.replace(/^(WO[-_\s]*)/i, "").trim();

            if (woNumber !== selectedNumber) return false;

            return (wo.lineItems || []).some((li) => {
              if (!li.budgetItem) return false;
              const ref =
                typeof li.budgetItem === "object"
                  ? li.budgetItem._id
                  : li.budgetItem;
              return ref === item._id;
            });
          });
        }

        if (!matched) return false;
      }

      return true;
    });
  };

  const handleAddToVariance = (varianceId) => {
    if (selectedLineItems.length === 0) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Please select at least one line item to add to the variance.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    setSelectedVarianceId(varianceId);
    setIsAddingToVariance(true);
    setShowVarianceModal(true);
  };

  const budgets = project.budgets || [];

  const filteredBudgets = budgets
    .filter((budget) => !budget.isVariance)
    .map((budget) => ({
      ...budget,
      lineItems:
        budget._id === expandedBudget
          ? applyFiltersToItems(budget.lineItems || [])
          : budget.lineItems || [],
    }));
  const calculateBreakdown = () => {
    const originalEstimatedCostBeforeVariance = budgets
      .filter(
        (budget) =>
          !budget.isVariance &&
          !budget.isChangeOrder &&
          !budget.isTimeAndMaterial
      )
      .flatMap((budget) => budget.lineItems || [])
      .reduce((sum, item) => sum + (item.amount || 0), 0);
    const tAndMCost = budgets
      .filter((budget) => budget.isTimeAndMaterial && !budget.isChangeOrder)
      .flatMap((budget) => budget.lineItems || [])
      .reduce((sum, item) => sum + (item.amount || 0), 0);
    const varianceImpact = budgets
      .filter(
        (budget) =>
          budget.isVariance &&
          budgets.find((b) => b._id === budget.originalBudgetId)
            ?.isChangeOrder !== true
      )
      .flatMap((budget) => budget.lineItems || [])
      .reduce((sum, item) => sum + (item.costDiff || 0), 0);
    const changeOrderImpact = budgets
      .filter((budget) => !budget.isVariance && budget.isChangeOrder)
      .reduce((sum, budget) => {
        const budgetAmount = (budget.lineItems || []).reduce(
          (acc, item) => acc + (item.amount || 0),
          0
        );
        const varianceCostDiff = budgets
          .filter((vb) => vb.isVariance && vb.originalBudgetId === budget._id)
          .flatMap((vb) => vb.lineItems || [])
          .reduce((acc, item) => acc + (item.costDiff || 0), 0);
        return sum + budgetAmount + varianceCostDiff;
      }, 0);
    const originalEstimatedCost =
      originalEstimatedCostBeforeVariance - varianceImpact;
    const totalEstimatedCost =
      originalEstimatedCostBeforeVariance + tAndMCost + changeOrderImpact;
    return {
      totalEstimatedCost: totalEstimatedCost.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      originalEstimatedCost: originalEstimatedCost.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      changeOrderImpact: changeOrderImpact.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      tAndMCost: tAndMCost.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      varianceImpact: varianceImpact.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    };
  };

  const {
    totalEstimatedCost: formattedTotal,
    originalEstimatedCost,
    changeOrderImpact,
    tAndMCost,
    varianceImpact,
  } = calculateBreakdown();

  const isUploadEnabled = budgetTitle.trim() !== "" && budgetFile !== null;

  const toggleBudgetItems = (budgetId) => {
    if (expandedBudget === budgetId) {
      setExpandedBudget(null);
      if (editingBudget === budgetId) setEditingBudget(null);
      setSelectedLineItems([]);
      clearAllFilters();
    } else {
      setExpandedBudget(budgetId);
      const mainBudget = budgets.find((b) => b._id === budgetId);
      if (
        mainBudget &&
        mainBudget.status !== "confirmed" &&
        !mainBudget.isVariance
      ) {
        handleEditBudget(budgetId);
      }
      setSelectedLineItems([]);
      clearAllFilters();
    }
  };

  const formatNumber = (num) =>
    (num ?? 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const isItemLinkedToPO = (budgetItemId) => {
    const item = project?.budgets
      ?.filter((budget) => !budget.isVariance)
      ?.flatMap((budget) => budget.lineItems)
      ?.find((i) => i._id === budgetItemId);
    return item?.linkedPO || false;
  };

  const isItemLinkedToWO = (budgetItemId, budgetId) => {
    const budget = project.budgets.find((b) => b._id === budgetId);
    if (budget?.isVariance) return false;
    if (!Array.isArray(project?.workOrders)) return false;
    return project.workOrders.some((wo) =>
      wo.lineItems.some((lineItem) => {
        if (!lineItem.budgetItem) return false;
        const budgetItemRef =
          typeof lineItem.budgetItem === "object" &&
          lineItem.budgetItem !== null &&
          lineItem.budgetItem._id
            ? lineItem.budgetItem._id
            : lineItem.budgetItem;
        return budgetItemRef === budgetItemId;
      })
    );
  };

  const renderChangesSummary = (
    changesSummary,
    quantityDiff,
    requiredQty,
    quantity,
    category // ← ADD category as parameter
  ) => {
    if (!changesSummary && quantityDiff === 0) return "No changes recorded.";

    const changes = [];

    const addChange = (field, oldValue, newValue, label, format = (v) => v) => {
      if (oldValue !== newValue && (oldValue != null || newValue != null)) {
        changes.push(
          `${label}: ${format(oldValue ?? "None")} → ${format(
            newValue ?? "None"
          )}`
        );
      }
    };

    // Existing field changes
    addChange(
      "sectionNumber",
      changesSummary.sectionNumber?.old,
      changesSummary.sectionNumber?.new,
      "Section #"
    );
    addChange(
      "costCodeDescription",
      changesSummary.costCodeDescription?.old,
      changesSummary.costCodeDescription?.new,
      "Description"
    );
    addChange(
      "itemCode",
      changesSummary.itemCode?.old,
      changesSummary.itemCode?.new,
      "Item Code"
    );
    addChange(
      "itemDescription",
      changesSummary.itemDescription?.old,
      changesSummary.itemDescription?.new,
      "Item Description"
    );
    addChange(
      "size",
      changesSummary.size?.old,
      changesSummary.size?.new,
      "Size"
    );
    addChange(
      "container",
      changesSummary.container?.old,
      changesSummary.container?.new,
      "Container"
    );
    addChange(
      "bidArea",
      changesSummary.bidArea?.old,
      changesSummary.bidArea?.new,
      "Bid Area"
    );
    addChange(
      "vendor",
      changesSummary.vendor?.old,
      changesSummary.vendor?.new,
      "Vendor"
    );
    addChange(
      "cost",
      changesSummary.cost?.old,
      changesSummary.cost?.new,
      "Cost",
      (v) => `$${formatNumber(v)}`
    );
    addChange(
      "unitRate",
      changesSummary.unitRate?.old,
      changesSummary.unitRate?.new,
      "Unit Rate",
      (v) => `$${formatNumber(v)}`
    );
    addChange(
      "price",
      changesSummary.price?.old,
      changesSummary.price?.new,
      "Price",
      (v) => `$${formatNumber(v)}`
    );
    addChange(
      "laborHours",
      changesSummary.laborHours?.old,
      changesSummary.laborHours?.new,
      "Labor Hours"
    );
    addChange("uom", changesSummary.uom?.old, changesSummary.uom?.new, "UOM");

    // === FIXED: Handle Quantity Change per Category ===
    if (quantityDiff !== 0) {
      let originalQty, newQty;

      if (category === "L") {
        // Labour: use `quantity` field
        originalQty = changesSummary.quantity?.old ?? quantity - quantityDiff;
        newQty = changesSummary.quantity?.new ?? quantity;
      } else {
        // Material: use `requiredQty`
        originalQty =
          changesSummary.requiredQty?.old ?? requiredQty - quantityDiff;
        newQty = changesSummary.requiredQty?.new ?? requiredQty;
      }

      changes.push(`Quantity: ${originalQty} → ${newQty}`);
    }

    return changes.length > 0 ? (
      <ul className="list-disc list-inside text-xs text-gray-400">
        {changes.map((change, idx) => (
          <li key={idx}>{change}</li>
        ))}
      </ul>
    ) : (
      "No changes recorded."
    );
  };

  const getFilteredBudgetItems = () => {
    if (!expandedBudget) return [];
    const budget = budgets.find((b) => b._id === expandedBudget);
    if (!budget) return [];

    const rawItems =
      editingBudget === expandedBudget ? editedItems : budget.lineItems || [];
    return applyFiltersToItems(rawItems);
  };

  return (
    <div className="space-y-6">
      {isMounted &&
        dialog.open &&
        createPortal(
          <AlertDialog
            type={dialog.type}
            title={dialog.title}
            message={dialog.message}
            confirmText={dialog.confirmText || "OK"}
            cancelText={dialog.cancelText || "Close"}
            open={dialog.open}
            onClose={
              dialog.onClose ||
              (() => setDialog((prev) => ({ ...prev, open: false })))
            }
            onConfirm={dialog.onConfirm}
          />,
          document.body
        )}
      {showVarianceModal && (
        <VarianceModal
          budgetId={expandedBudget}
          lineItems={selectedLineItems}
          vendors={vendors}
          bidAreaOptions={getFilterOptions("bidArea")}
          projectId={project._id}
          varianceId={isAddingToVariance ? selectedVarianceId : null}
          isAddingToVariance={isAddingToVariance}
          onClose={() => {
            setShowVarianceModal(false);
            setSelectedLineItems([]);
            setIsAddingToVariance(false);
            setSelectedVarianceId(null);
          }}
          onVarianceCreated={(updatedProject) => {
            setProject(updatedProject);
            setShowVarianceModal(false);
            setSelectedLineItems([]);
            setIsAddingToVariance(false);
            setSelectedVarianceId(null);
          }}
          setProject={setProject}
          setDialog={setDialog}
        />
      )}
      <div className="bg-gray-700 p-4 rounded-lg shadow mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-md font-semibold text-gray-200">
            Budget Management
          </h3>
          <div className="flex space-x-2">
            {project.budgets?.length > 0 && (
              <button
                onClick={onGeneratePO}
                disabled={
                  project.budgets[0]?.status === "pending" ||
                  !budgets.some(
                    (budget) =>
                      !budget.isVariance &&
                      (budget.lineItems || []).some(
                        (item) =>
                          !isItemLinkedToPO(item._id, budget._id) &&
                          !isItemLinkedToWO(item._id, budget._id)
                      )
                  )
                }
                className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
                  project.budgets[0]?.status === "pending" ||
                  !budgets.some(
                    (budget) =>
                      !budget.isVariance &&
                      (budget.lineItems || []).some(
                        (item) =>
                          !isItemLinkedToPO(item._id, budget._id) &&
                          !isItemLinkedToWO(item._id, budget._id)
                      )
                  )
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-orange-400 hover:bg-orange-500"
                }`}
              >
                Generate PO or WO
              </button>
            )}
            {budgets.some((b) => b.isTimeAndMaterial || b.isVariance) && (
              <button
                onClick={handleConvertMultipleToChangeOrder}
                disabled={selectedBudgets.length === 0}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
                  selectedBudgets.length > 0
                    ? "bg-orange-400 hover:bg-orange-500"
                    : "bg-gray-600 cursor-not-allowed"
                }`}
              >
                Convert Selected to Change Order
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          <div>
            <p className="text-sm text-gray-400">Total Estimated Cost</p>
            <p className="text-lg font-bold text-gray-200">${formattedTotal}</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Original Estimated Cost</p>
            <p className="text-lg font-medium text-gray-200">
              ${originalEstimatedCost}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-400">T&M Cost</p>
            <p className="text-lg font-medium text-gray-200">${tAndMCost}</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Change Order Impact</p>
            <p className="text-lg font-medium text-gray-200">
              ${changeOrderImpact}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Variance Impact</p>
            <p className="text-lg font-medium text-gray-200">
              ${varianceImpact}
            </p>
          </div>
        </div>
      </div>
      {project.budgets?.length === 0 && (
        <div className="flex space-x-4 mb-4">
          <button
            onClick={() => setShowBudgetForm(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
          >
            Create New Budget
          </button>
        </div>
      )}
      {project.budgets?.length === 0 && showBudgetForm && (
        <div className="space-y-4 bg-gray-700 shadow-lg rounded-lg p-6">
          <input
            type="text"
            value={budgetTitle}
            onChange={(e) => setBudgetTitle(e.target.value)}
            placeholder="Budget Title"
            className="block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
          />
          <input
            type="file"
            accept=".csv, .xlsx"
            onChange={(e) => setBudgetFile(e.target.files[0])}
            className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-gray-200 hover:file:bg-gray-500"
          />
          <button
            onClick={handleFileUpload}
            disabled={!isUploadEnabled || isUploading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
              isUploadEnabled && !isUploading
                ? "bg-orange-400 hover:bg-orange-500"
                : "bg-gray-600 cursor-not-allowed"
            }`}
          >
            {isUploading ? "Uploading..." : "Upload Budget"}
          </button>
        </div>
      )}
      <div>
        {budgets.length === 0 ? (
          <p className="text-gray-400 text-sm">No budgets available.</p>
        ) : (
          <div className="space-y-4">
            {budgets
              .filter((budget) => !budget.isVariance)
              .map((budget) => {
                const filteredLineItems =
                  budget._id === expandedBudget
                    ? (budget.lineItems || [])
                        .filter((item) => {
                          if (!filters.category) return true;
                          return item.category === filters.category;
                        })
                        .filter((item) => {
                          if (!filters.section) return true;
                          return item.sectionNumber === filters.section;
                        })
                        .filter((item) => {
                          if (!filters.bidArea) return true;
                          return item.bidArea === filters.bidArea;
                        })
                        .filter((item) => {
                          if (!filters.vendor) return true;
                          const vendorId =
                            item.vendor && typeof item.vendor === "object"
                              ? item.vendor._id
                              : item.vendor || null;
                          return (
                            item.category === "M" && vendorId === filters.vendor
                          );
                        })
                        .filter((item) => {
                          if (!filters.descriptionSearch) return true;
                          const description =
                            item.itemDescription ||
                            item.costCodeDescription ||
                            "";
                          return description
                            .toLowerCase()
                            .includes(filters.descriptionSearch.toLowerCase());
                        })
                    : budget.lineItems || [];
                const budgetTotal = (budget.lineItems || [])
                  .reduce((sum, item) => sum + (item.amount || 0), 0)
                  .toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                const varianceBudgets = budgets.filter(
                  (vb) =>
                    budget.varianceBudgets?.some((vbid) => vbid === vb._id) ||
                    vb.originalBudgetId === budget._id
                );
                return (
                  <div
                    key={budget._id}
                    className={`border border-gray-600 rounded-lg shadow-lg ${
                      budget.isChangeOrder
                        ? "bg-gray-700"
                        : budget.isTimeAndMaterial
                        ? "bg-gray-700"
                        : "bg-gray-700"
                    }`}
                  >
                    <div className="px-6 py-4 flex justify-between items-center">
                      <div className="flex items-center space-x-3">
                        {(budget.isTimeAndMaterial || budget.isVariance) &&
                          !budget.isChangeOrder && (
                            <input
                              type="checkbox"
                              checked={selectedBudgets.includes(budget._id)}
                              onChange={() => handleSelectBudget(budget._id)}
                              className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                            />
                          )}
                        <div>
                          <h4 className="text-md font-semibold text-white">
                            {budget.title}
                            {budget.isChangeOrder
                              ? " (Change Order)"
                              : budget.isTimeAndMaterial
                              ? " (T&M)"
                              : ` - (${budget.status})`}
                          </h4>
                          <p className="text-sm text-gray-400">
                            Total Amount: ${budgetTotal}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => toggleBudgetItems(budget._id)}
                          className="px-3 py-1 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                        >
                          {expandedBudget === budget._id
                            ? "Collapse"
                            : "Expand"}
                        </button>
                        {budget.status === "pending" && (
                          <button
                            onClick={() =>
                              handleConfirmBudgetPrompt(budget._id)
                            }
                            className="px-3 py-1 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                          >
                            Confirm
                          </button>
                        )}
                        {!budget.isChangeOrder &&
                          budget.status !== "confirmed" && (
                            <button
                              onClick={() =>
                                editingBudget === budget._id
                                  ? handleCancelEdit()
                                  : handleEditBudget(budget._id)
                              }
                              className={`px-3 py-1 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
                                editingBudget === budget._id
                                  ? "bg-gray-600 hover:bg-gray-500"
                                  : "bg-orange-400 hover:bg-orange-500"
                              }`}
                            >
                              {editingBudget === budget._id
                                ? "Cancel"
                                : "Edit Budget"}
                            </button>
                          )}
                        {!budget.isChangeOrder &&
                          budget.status !== "confirmed" && (
                            <button
                              onClick={() =>
                                handleDeleteBudgetPrompt(budget._id)
                              }
                              className="p-2 text-red-400 hover:text-red-300 transition-colors duration-200"
                              title="Delete Budget"
                            >
                              <FiTrash2 size={18} />
                            </button>
                          )}
                        {!budget.isChangeOrder &&
                          budget.status === "confirmed" &&
                          selectedBudgets.length === 0 && (
                            <button
                              disabled={selectedBudgets.length > 0}
                              onClick={() =>
                                handleConvertToChangeOrder(budget, budget._id)
                              }
                              className={`px-3 py-1 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200 ${
                                selectedBudgets.length > 0
                                  ? "bg-gray-600 cursor-not-allowed"
                                  : "bg-orange-400 hover:bg-orange-500"
                              }`}
                            >
                              Convert to Change Order
                            </button>
                          )}
                      </div>
                    </div>
                    {expandedBudget === budget._id && (
                      <div className="px-2 pb-4 overflow-x-auto">
                        <div className="flex justify-between items-center mb-4">
                          <button
                            onClick={clearAllFilters}
                            className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                          >
                            Clear Filters
                          </button>
                          {budget.status === "confirmed" &&
                            (!budget.isTimeAndMaterial ||
                              budget.isChangeOrder) && (
                              <button
                                onClick={handleCreateVariances}
                                disabled={selectedLineItems.length === 0}
                                className={`px-3 py-1 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
                                  selectedLineItems.length > 0
                                    ? "bg-orange-400 hover:bg-orange-500"
                                    : "bg-gray-600 cursor-not-allowed"
                                }`}
                              >
                                Create Variance
                                {selectedLineItems.length > 1 ? "s" : ""} (
                                {selectedLineItems.length})
                              </button>
                            )}
                        </div>
                        <table className="min-w-full divide-y divide-gray-600 text-xs">
                          <thead className="bg-gray-800">
                            <tr>
                              {(budget.status === "confirmed" ||
                                editingBudget === budget._id) &&
                                (!budget.isTimeAndMaterial ||
                                  budget.isChangeOrder) && (
                                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                    Select
                                  </th>
                                )}
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Item #
                              </th>
                              <th
                                className={`px-2 py-2 text-left text-xs font-medium cursor-pointer relative ${
                                  filters.section
                                    ? "text-orange-400"
                                    : "text-gray-400"
                                }`}
                                onClick={() => toggleFilter("section")}
                              >
                                Section #
                                {showFilter === "section" && (
                                  <div
                                    ref={sectionRef}
                                    className="absolute z-10 mt-2 bg-gray-600 border border-gray-500 rounded-md shadow-lg w-48"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="p-2">
                                      <Select
                                        options={getFilterOptions(
                                          "sectionNumber"
                                        )}
                                        value={
                                          getFilterOptions(
                                            "sectionNumber"
                                          ).find(
                                            (option) =>
                                              option.value === filters.section
                                          ) || null
                                        }
                                        onChange={(option) =>
                                          handleFilterChange(
                                            "section",
                                            option?.value || null
                                          )
                                        }
                                        placeholder="Filter Section"
                                        isClearable
                                        className="text-xs"
                                        styles={customStyles}
                                        onMenuOpen={() =>
                                          setShowFilter("section")
                                        }
                                        onMenuClose={() => setShowFilter(null)}
                                      />
                                    </div>
                                  </div>
                                )}
                              </th>
                              <th
                                className={`px-2 py-2 text-left text-xs font-medium cursor-pointer relative ${
                                  filters.category
                                    ? "text-orange-400"
                                    : "text-gray-400"
                                }`}
                                onClick={() => toggleFilter("category")}
                              >
                                Cat
                                {showFilter === "category" && (
                                  <div
                                    ref={categoryRef}
                                    className="absolute z-10 mt-2 bg-gray-600 border border-gray-500 rounded-md shadow-lg w-48"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="p-2">
                                      <Select
                                        options={getFilterOptions("category")}
                                        value={
                                          getFilterOptions("category").find(
                                            (option) =>
                                              option.value === filters.category
                                          ) || null
                                        }
                                        onChange={(option) =>
                                          handleFilterChange(
                                            "category",
                                            option?.value || null
                                          )
                                        }
                                        placeholder="Filter Category"
                                        isClearable
                                        className="text-xs"
                                        styles={customStyles}
                                        onMenuOpen={() =>
                                          setShowFilter("category")
                                        }
                                        onMenuClose={() => setShowFilter(null)}
                                      />
                                    </div>
                                  </div>
                                )}
                              </th>
                              <th
                                className={`px-2 py-2 text-left text-xs font-medium cursor-pointer relative w-1/4 ${
                                  filters.descriptionSearch
                                    ? "text-orange-400"
                                    : "text-gray-400"
                                }`}
                                onClick={() =>
                                  toggleFilter("descriptionSearch")
                                }
                              >
                                Description
                                {showFilter === "descriptionSearch" && (
                                  <div
                                    ref={descriptionRef}
                                    className="absolute z-10 mt-2 bg-gray-600 border border-gray-500 rounded-md shadow-lg w-64"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="p-2">
                                      <input
                                        type="text"
                                        value={filters.descriptionSearch}
                                        onChange={(e) =>
                                          handleFilterChange(
                                            "descriptionSearch",
                                            e.target.value
                                          )
                                        }
                                        placeholder="Search description..."
                                        className="w-full rounded-md bg-gray-700 border-gray-500 text-gray-200 text-xs py-1 px-2"
                                      />
                                    </div>
                                  </div>
                                )}
                              </th>

                              <th
                                className={`px-2 py-2 text-left text-xs font-medium cursor-pointer relative ${
                                  filters.size
                                    ? "text-orange-400"
                                    : "text-gray-400"
                                }`}
                                onClick={() => toggleFilter("size")}
                              >
                                SIZE
                                {showFilter === "size" && (
                                  <div
                                    ref={sizeRef}
                                    className="absolute z-10 mt-2 bg-gray-600 border border-gray-500 rounded-md shadow-lg w-48"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="p-2">
                                      <Select
                                        options={getFilterOptions("size")}
                                        value={
                                          getFilterOptions("size").find(
                                            (o) => o.value === filters.size
                                          ) || null
                                        }
                                        onChange={(opt) =>
                                          handleFilterChange(
                                            "size",
                                            opt?.value || null
                                          )
                                        }
                                        placeholder="Filter Size"
                                        isClearable
                                        className="text-xs"
                                        styles={customStyles}
                                        onMenuOpen={() => setShowFilter("size")}
                                        onMenuClose={() => setShowFilter(null)}
                                      />
                                    </div>
                                  </div>
                                )}
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Qty
                              </th>
                              <th
                                className={`px-2 py-2 text-left text-xs font-medium cursor-pointer relative ${
                                  filters.uom
                                    ? "text-orange-400"
                                    : "text-gray-400"
                                }`}
                                onClick={() => toggleFilter("uom")}
                              >
                                UOM
                                {showFilter === "uom" && (
                                  <div
                                    ref={uomRef}
                                    className="absolute z-10 mt-2 bg-gray-600 border border-gray-500 rounded-md shadow-lg w-48"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="p-2">
                                      <Select
                                        options={getFilterOptions("uom")}
                                        value={
                                          getFilterOptions("uom").find(
                                            (o) => o.value === filters.uom
                                          ) || null
                                        }
                                        onChange={(opt) =>
                                          handleFilterChange(
                                            "uom",
                                            opt?.value || null
                                          )
                                        }
                                        placeholder="Filter UOM"
                                        isClearable
                                        className="text-xs"
                                        styles={customStyles}
                                        onMenuOpen={() => setShowFilter("uom")}
                                        onMenuClose={() => setShowFilter(null)}
                                      />
                                    </div>
                                  </div>
                                )}
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Cost/Rate
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Customer Price
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Subtotal Cost
                              </th>
                              <th
                                className={`px-2 py-2 text-left text-xs font-medium cursor-pointer relative ${
                                  filters.bidArea
                                    ? "text-orange-400"
                                    : "text-gray-400"
                                }`}
                                onClick={() => toggleFilter("bidArea")}
                              >
                                BID AREA
                                {showFilter === "bidArea" && (
                                  <div
                                    ref={bidAreaRef}
                                    className="absolute z-10 mt-2 bg-gray-600 border border-gray-500 rounded-md shadow-lg w-48"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="p-2">
                                      <Select
                                        options={getFilterOptions("bidArea")}
                                        value={
                                          getFilterOptions("bidArea").find(
                                            (option) =>
                                              option.value === filters.bidArea
                                          ) || null
                                        }
                                        onChange={(option) =>
                                          handleFilterChange(
                                            "bidArea",
                                            option?.value || null
                                          )
                                        }
                                        placeholder="Filter Bid Area"
                                        isClearable
                                        className="text-xs"
                                        styles={customStyles}
                                        onMenuOpen={() =>
                                          setShowFilter("bidArea")
                                        }
                                        onMenuClose={() => setShowFilter(null)}
                                      />
                                    </div>
                                  </div>
                                )}
                              </th>
                              <th
                                className={`px-2 py-2 text-left text-xs font-medium cursor-pointer relative ${
                                  filters.vendor
                                    ? "text-orange-400"
                                    : "text-gray-400"
                                }`}
                                onClick={() => toggleFilter("vendor")}
                              >
                                VENDOR
                                {showFilter === "vendor" && (
                                  <div
                                    ref={vendorRef}
                                    className="absolute z-10 mt-2 bg-gray-600 border border-gray-500 rounded-md shadow-lg w-48"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="p-2">
                                      <Select
                                        options={getFilterOptions("vendor")}
                                        value={
                                          getFilterOptions("vendor").find(
                                            (option) =>
                                              option.value === filters.vendor
                                          ) || null
                                        }
                                        onChange={(option) =>
                                          handleFilterChange(
                                            "vendor",
                                            option?.value || null
                                          )
                                        }
                                        placeholder="Filter Vendor"
                                        isClearable
                                        className="text-xs"
                                        styles={customStyles}
                                        onMenuOpen={() =>
                                          setShowFilter("vendor")
                                        }
                                        onMenuClose={() => setShowFilter(null)}
                                      />
                                    </div>
                                  </div>
                                )}
                              </th>

                              <th
                                className={`px-2 py-2 text-left text-xs font-medium cursor-pointer relative ${
                                  filters.poWo
                                    ? "text-orange-400"
                                    : "text-gray-400"
                                }`}
                                onClick={() => toggleFilter("poWo")}
                              >
                                PO/WO
                                {showFilter === "poWo" && (
                                  <div
                                    ref={poWoRef}
                                    className="absolute z-10 mt-2 bg-gray-600 border border-gray-500 rounded-md shadow-lg w-48 right-0" // right-0 aligns to right edge
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="p-2">
                                      <Select
                                        options={getFilterOptions("poWo")}
                                        value={
                                          getFilterOptions("poWo").find(
                                            (o) => o.value === filters.poWo
                                          ) || null
                                        }
                                        onChange={(opt) =>
                                          handleFilterChange(
                                            "poWo",
                                            opt?.value || null
                                          )
                                        }
                                        placeholder="Filter PO/WO"
                                        isClearable
                                        className="text-xs"
                                        menuPlacement="auto" // Let react-select decide top/bottom
                                        menuPosition="fixed" // Prevent scroll issues
                                        menuPortalTarget={document.body} // Render outside table
                                        styles={customStyles}
                                        onMenuOpen={() => setShowFilter("poWo")}
                                        onMenuClose={() => setShowFilter(null)}
                                      />
                                    </div>
                                  </div>
                                )}
                              </th>
                              {editingBudget === budget._id &&
                                budget.status !== "confirmed" && (
                                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                    Actions
                                  </th>
                                )}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-600">
                            {getFilteredBudgetItems().length === 0 ? (
                              <tr>
                                <td
                                  colSpan={
                                    (budget.status === "confirmed" ||
                                      editingBudget === budget._id) &&
                                    (!budget.isTimeAndMaterial ||
                                      budget.isChangeOrder)
                                      ? 15 // +1 for PO/WO
                                      : 14
                                  }
                                  className="px-2 py-4 text-center text-sm text-gray-400"
                                >
                                  No items match the current filters.
                                </td>
                              </tr>
                            ) : (
                              getFilteredBudgetItems().map((item, index) => {
                                const originalIndex = (
                                  editingBudget === budget._id
                                    ? editedItems
                                    : budget.lineItems || []
                                ).findIndex((i) => i._id === item._id);
                                const vendorId =
                                  editingBudget === budget._id
                                    ? item.vendor
                                    : item.vendor &&
                                      typeof item.vendor === "object" &&
                                      item.vendor._id
                                    ? item.vendor._id
                                    : typeof item.vendor === "string"
                                    ? item.vendor
                                    : null;

                                const isLinkedToPO = isItemLinkedToPO(item._id);
                                const isLinkedToWO = isItemLinkedToWO(
                                  item._id,
                                  budget._id
                                );
                                const isConfirmed =
                                  budget.status === "confirmed";
                                const displayUOM =
                                  item.category === "M"
                                    ? item.container
                                    : item.uom;

                                return (
                                  <tr
                                    key={item._id}
                                    className="hover:bg-gray-600 transition-colors duration-200"
                                  >
                                    {/* Select Checkbox */}
                                    {(isConfirmed ||
                                      editingBudget === budget._id) &&
                                      (!budget.isTimeAndMaterial ||
                                        budget.isChangeOrder) && (
                                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                          <input
                                            type="checkbox"
                                            checked={selectedLineItems.some(
                                              (i) => i._id === item._id
                                            )}
                                            onChange={() =>
                                              handleSelectLineItem(item)
                                            }
                                            className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                                          />
                                        </td>
                                      )}

                                    {/* Item # */}
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                      {originalIndex + 1}
                                    </td>

                                    {/* Section # */}
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                      {item.sectionNumber}
                                    </td>

                                    {/* Category */}
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                      {item.category}
                                    </td>

                                    {/* Description */}
                                    <td className="px-2 py-2 text-xs text-gray-200 w-1/4">
                                      {item.itemDescription ||
                                        item.costCodeDescription}
                                    </td>

                                    {/* Size */}
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                      {item.size || "N/A"}
                                    </td>

                                    {/* Quantity */}
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                      {editingBudget === budget._id &&
                                      !isConfirmed ? (
                                        <input
                                          type="number"
                                          step={
                                            item.category === "M" ? "1" : "0.01"
                                          }
                                          value={
                                            item.category === "L"
                                              ? item.quantity
                                              : item.requiredQty
                                          }
                                          onChange={(e) =>
                                            handleItemChange(
                                              originalIndex,
                                              item.category === "L"
                                                ? "quantity"
                                                : "requiredQty",
                                              e.target.value
                                            )
                                          }
                                          className="w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 text-xs py-0.5 px-1"
                                        />
                                      ) : item.category === "L" ? (
                                        item.quantity
                                      ) : (
                                        item.requiredQty
                                      )}
                                    </td>

                                    {/* UOM */}
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                      {displayUOM || "N/A"}
                                    </td>

                                    {/* Cost/Rate */}
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                      {item.category === "M" ? (
                                        editingBudget === budget._id &&
                                        !isConfirmed ? (
                                          <input
                                            type="number"
                                            step="0.01"
                                            value={item.cost || 0}
                                            onChange={(e) =>
                                              handleItemChange(
                                                originalIndex,
                                                "cost",
                                                e.target.value
                                              )
                                            }
                                            className="w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 text-xs py-0.5 px-1"
                                          />
                                        ) : (
                                          `$${formatNumber(item.cost || 0)}`
                                        )
                                      ) : editingBudget === budget._id &&
                                        !isConfirmed ? (
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={item.unitRate || 0}
                                          onChange={(e) =>
                                            handleItemChange(
                                              originalIndex,
                                              "unitRate",
                                              e.target.value
                                            )
                                          }
                                          className="w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 text-xs py-0.5 px-1"
                                        />
                                      ) : (
                                        `$${formatNumber(item.unitRate || 0)}`
                                      )}
                                    </td>

                                    {/* Customer Price */}
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                      {editingBudget === budget._id &&
                                      !isConfirmed ? (
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={item.price || 0}
                                          onChange={(e) =>
                                            handleItemChange(
                                              originalIndex,
                                              "price",
                                              e.target.value
                                            )
                                          }
                                          className="w-20 rounded-md bg-gray-600 border-gray-500 text-gray-200 text-xs py-0.5 px-1"
                                        />
                                      ) : (
                                        `$${formatNumber(item.price || 0)}`
                                      )}
                                    </td>

                                    {/* Subtotal Cost */}
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                      ${formatNumber(item.amount || 0)}
                                    </td>

                                    {/* Bid Area */}
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                      {editingBudget === budget._id &&
                                      !isConfirmed ? (
                                        <Select
                                          options={getFilterOptions("bidArea")}
                                          value={
                                            getFilterOptions("bidArea").find(
                                              (o) => o.value === item.bidArea
                                            ) || null
                                          }
                                          onChange={(opt) =>
                                            handleItemChange(
                                              originalIndex,
                                              "bidArea",
                                              opt?.value || ""
                                            )
                                          }
                                          placeholder="Select"
                                          isClearable
                                          styles={customStyles}
                                          className="text-xs"
                                        />
                                      ) : (
                                        item.bidArea || "N/A"
                                      )}
                                    </td>

                                    {/* Vendor */}
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                      {item.category === "M" ? (
                                        editingBudget === budget._id &&
                                        !isConfirmed ? (
                                          <Select
                                            options={getFilterOptions("vendor")}
                                            value={
                                              getFilterOptions("vendor").find(
                                                (o) => o.value === vendorId
                                              ) || null
                                            }
                                            onChange={(opt) =>
                                              handleItemChange(
                                                originalIndex,
                                                "vendor",
                                                opt?.value || null
                                              )
                                            }
                                            placeholder="Select"
                                            isClearable
                                            styles={customStyles}
                                            className="text-xs"
                                          />
                                        ) : (
                                          vendors.find(
                                            (v) => v._id === vendorId
                                          )?.companyName || "Unassigned"
                                        )
                                      ) : (
                                        "N/A"
                                      )}
                                    </td>

                                    {/* PO/WO */}
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                      {(() => {
                                        const po = project.purchaseOrders?.find(
                                          (po) =>
                                            po.lineItems?.some((li) => {
                                              const ref =
                                                typeof li.budgetItem ===
                                                "object"
                                                  ? li.budgetItem?._id
                                                  : li.budgetItem;
                                              return ref === item._id;
                                            })
                                        );
                                        const wo = project.workOrders?.find(
                                          (wo) =>
                                            wo.lineItems?.some((li) => {
                                              const ref =
                                                typeof li.budgetItem ===
                                                "object"
                                                  ? li.budgetItem?._id
                                                  : li.budgetItem;
                                              return ref === item._id;
                                            })
                                        );
                                        const parts = [];
                                        if (po?.poNumber)
                                          parts.push(
                                            <span
                                              key="po"
                                              className="text-orange-400"
                                            >
                                              PO-{po.poNumber}
                                            </span>
                                          );
                                        if (wo?.woNumber)
                                          parts.push(
                                            <span
                                              key="wo"
                                              className="text-green-400"
                                            >
                                              WO-{wo.woNumber}
                                            </span>
                                          );
                                        return parts.length > 0 ? parts : "—";
                                      })()}
                                    </td>

                                    {/* Actions (Delete) */}
                                    {editingBudget === budget._id &&
                                      budget.status !== "confirmed" && (
                                        <td className="px-2 py-2 text-right">
                                          <button
                                            onClick={() =>
                                              handleRemoveItem(originalIndex)
                                            }
                                            disabled={
                                              isLinkedToPO || isLinkedToWO
                                            }
                                            className={`text-red-400 hover:text-red-300 ${
                                              isLinkedToPO || isLinkedToWO
                                                ? "opacity-50 cursor-not-allowed"
                                                : ""
                                            }`}
                                          >
                                            <TrashIcon className="h-5 w-5" />
                                          </button>
                                        </td>
                                      )}
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                        {editingBudget === budget._id && !budget.isVariance && (
                          <div className="mt-4 flex justify-end space-x-2">
                            <button
                              onClick={saveEditedBudget}
                              className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                            >
                              Save Budget
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {varianceBudgets.length > 0 ? (
                      <div className="px-6 py-4">
                        <h5 className="text-sm font-semibold text-white mb-2">
                          Related Variances
                        </h5>
                        {varianceBudgets.map((variance) => {
                          const varianceSummary = (
                            variance.lineItems || []
                          ).reduce(
                            (acc, item) => ({
                              totalQtyDiff:
                                acc.totalQtyDiff + (item.quantityDiff || 0),
                              totalPriceDiff:
                                acc.totalPriceDiff + (item.priceDiff || 0),
                              totalCostDiff:
                                acc.totalCostDiff + (item.costDiff || 0),
                            }),
                            {
                              totalQtyDiff: 0,
                              totalPriceDiff: 0,
                              totalCostDiff: 0,
                            }
                          );
                          return (
                            <div
                              key={variance._id}
                              className={`border border-gray-600 rounded-lg shadow-lg mt-2 ${
                                variance.isConverted
                                  ? "bg-gray-100 opacity-75"
                                  : "bg-gray-800"
                              }`}
                            >
                              <div className="px-4 py-3 flex justify-between items-center">
                                <div className="flex items-center space-x-3">
                                  {!variance.isConverted && (
                                    <input
                                      type="checkbox"
                                      checked={selectedBudgets.includes(
                                        variance._id
                                      )}
                                      onChange={() =>
                                        handleSelectBudget(variance._id)
                                      }
                                      className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                                    />
                                  )}
                                  <div>
                                    <h6 className="text-sm font-semibold text-white">
                                      {variance.title} (Variance
                                      {variance.isConverted
                                        ? " - Converted"
                                        : ""}
                                      )
                                    </h6>
                                    {variance.varianceDescription && (
                                      <p className="text-xs text-gray-400 mt-1">
                                        Reason: {variance.varianceDescription}
                                      </p>
                                    )}
                                    {variance.isConverted && (
                                      <p className="text-xs text-gray-400 mt-1">
                                        Converted to Change Order:{" "}
                                        {variance.convertedToChangeOrderId}
                                      </p>
                                    )}
                                    <div className="text-xs text-gray-400 mt-1">
                                      {varianceSummary.totalQtyDiff !== 0 && (
                                        <p>
                                          Total Quantity Difference:{" "}
                                          {varianceSummary.totalQtyDiff}
                                        </p>
                                      )}
                                      {varianceSummary.totalPriceDiff !== 0 && (
                                        <p>
                                          Total Price Difference: $
                                          {formatNumber(
                                            varianceSummary.totalPriceDiff
                                          )}
                                        </p>
                                      )}
                                      {varianceSummary.totalCostDiff !== 0 && (
                                        <p>
                                          Total Cost Difference: $
                                          {formatNumber(
                                            varianceSummary.totalCostDiff
                                          )}
                                        </p>
                                      )}
                                      {varianceSummary.totalQtyDiff === 0 &&
                                        varianceSummary.totalPriceDiff === 0 &&
                                        varianceSummary.totalCostDiff === 0 && (
                                          <p>No changes recorded.</p>
                                        )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() =>
                                      toggleBudgetItems(variance._id)
                                    }
                                    className="px-2 py-1 text-xs font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                                  >
                                    {expandedBudget === variance._id
                                      ? "Collapse"
                                      : "Expand"}
                                  </button>
                                  {/* {!variance.isConverted &&
                                    !budget.isChangeOrder && (
                                      <button
                                        onClick={() =>
                                          handleConvertToChangeOrder(
                                            variance,
                                            budget._id
                                          )
                                        }
                                        className="px-2 py-1 text-xs font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                                      >
                                        Convert to Change Order
                                      </button>
                                    )} */}
                                </div>
                              </div>
                              {expandedBudget === variance._id && (
                                <div className="px-4 pb-3 overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-600 text-xs">
                                    <thead className="bg-gray-800">
                                      <tr>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Section #
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Cat
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-1/4">
                                          Description
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Size
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Qty Diff
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Original Qty
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Updated Qty
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Cost/Rate
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Unit Rate
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Price
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Price Diff
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Cost Diff
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Bid Area
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Vendor
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                          Changes Summary
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-600">
                                      {(variance.lineItems || []).map(
                                        (item) => {
                                          const originalQty =
                                            item.category === "M"
                                              ? item.changesSummary?.requiredQty
                                                  ?.old ??
                                                item.requiredQty -
                                                  item.quantityDiff ??
                                                0
                                              : item.changesSummary?.quantity
                                                  ?.old ??
                                                item.quantity -
                                                  item.quantityDiff ??
                                                0;
                                          const updatedQty =
                                            item.category === "M"
                                              ? item.changesSummary?.requiredQty
                                                  ?.old != null
                                                ? item.changesSummary
                                                    .requiredQty.old +
                                                  item.quantityDiff
                                                : item.requiredQty ?? 0
                                              : item.changesSummary?.quantity
                                                  ?.old != null
                                              ? item.changesSummary.quantity
                                                  .old + item.quantityDiff
                                              : item.quantity ?? 0;
                                          return (
                                            <tr
                                              key={item._id}
                                              className="hover:bg-gray-600 transition-colors duration-200"
                                            >
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                {item.sectionNumber}
                                              </td>
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                {item.category}
                                              </td>
                                              <td className="px-2 py-2 text-xs text-gray-200 w-1/4">
                                                {item.costCodeDescription}
                                              </td>
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                {item.size || "N/A"}
                                              </td>
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                {item.quantityDiff}
                                              </td>
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                {originalQty}
                                              </td>
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                {updatedQty}
                                              </td>
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                {item.category === "M"
                                                  ? `$${formatNumber(
                                                      item.cost || 0
                                                    )}`
                                                  : `$${formatNumber(
                                                      item.unitRate || 0
                                                    )}`}
                                              </td>
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                {item.category === "L"
                                                  ? `$${formatNumber(
                                                      item.changesSummary
                                                        ?.unitRate?.new || 0
                                                    )}`
                                                  : "N/A"}
                                              </td>
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                {`$${formatNumber(
                                                  item.changesSummary?.price
                                                    ?.new ||
                                                    item.price ||
                                                    0
                                                )}`}
                                              </td>
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                ${formatNumber(item.priceDiff)}
                                              </td>
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                ${formatNumber(item.costDiff)}
                                              </td>
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                {item.bidArea || "N/A"}
                                              </td>
                                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-200">
                                                {item.category === "M"
                                                  ? item.vendor?.companyName ||
                                                    "Unassigned"
                                                  : "N/A"}
                                              </td>
                                              <td className="px-2 py-2 text-xs text-gray-200">
                                                {renderChangesSummary(
                                                  item.changesSummary,
                                                  item.quantityDiff,
                                                  item.requiredQty,
                                                  item.quantity,
                                                  item.category // ← ADD THIS
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        }
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="px-6 py-4">
                        <p className="text-sm text-gray-400">
                          No related variances found.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}
